import styled, { keyframes } from 'styled-components';

/* ─── Animaciones ───────────────────────────────────────────────────────── */
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-12px); }
`;

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`;

/* ─── HERO: split layout ─────────────────────────────────────────────────── */

export const HeroSection = styled.section`
  background: var(--gradient-hero);
  position: relative;
  overflow: hidden;
  min-height: 560px;
  display: flex;
  align-items: center;

  &::after {
    content: '';
    position: absolute;
    top: 0; right: 0;
    width: 46%;
    height: 100%;
    background: var(--bg-section);
    border-left: 1px solid var(--border-light);
    border-bottom: 1px solid var(--border-light);
    border-radius: 0 0 0 80px;
    z-index: 0;
  }

  @media (max-width: 900px) {
    min-height: auto;
    &::after { display: none; }
  }
`;

export const HeroInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 80px 24px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  align-items: center;
  position: relative;
  z-index: 1;
  width: 100%;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    padding: 56px 20px;
  }
`;

export const HeroLeft = styled.div`
  padding-right: 48px;
  animation: ${fadeUp} 0.6s ease both;

  @media (max-width: 900px) {
    padding-right: 0;
    text-align: center;
  }
`;

export const HeroChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  padding: 6px 16px;
  border-radius: var(--radius-round);
  margin-bottom: 20px;

  &::before {
    content: '';
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--accent);
    flex-shrink: 0;
  }
`;

export const HeroTitle = styled.h1`
  font-size: clamp(34px, 4vw, 52px);
  font-weight: 700;
  color: var(--text-primary);
  line-height: 1.1;
  letter-spacing: -1.5px;
  margin-bottom: 20px;

  em {
    font-style: normal;
    color: var(--secondary);
  }
`;

export const HeroSubtitle = styled.p`
  font-size: var(--text-lg);
  color: var(--text-secondary);
  line-height: 1.65;
  margin-bottom: 36px;
  max-width: 420px;

  @media (max-width: 900px) {
    margin-left: auto;
    margin-right: auto;
  }
`;

export const SearchSection = styled.div`
  display: flex;
  align-items: center;
  background: var(--bg-card);
  border: 2px solid var(--border-medium);
  border-radius: var(--radius-round);
  padding: 6px 6px 6px 20px;
  box-shadow: var(--shadow-md);
  max-width: 460px;
  transition: border-color var(--transition-fast), box-shadow var(--transition-fast);

  &:focus-within {
    border-color: var(--primary);
    box-shadow: 0 0 0 4px var(--focus-ring);
  }

  @media (max-width: 900px) {
    max-width: 100%;
  }

  @media (max-width: 560px) {
    flex-direction: column;
    border-radius: var(--radius-lg);
    padding: 12px;
    gap: 10px;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  color: var(--text-primary);
  font-size: var(--text-base);
  font-family: var(--font-brand);
  padding: 10px 4px;
  min-width: 0;

  &::placeholder { color: var(--text-muted); }
  &:focus { outline: none; }

  @media (max-width: 560px) {
    width: 100%;
    padding: 10px 12px;
    background: var(--bg-input);
    border-radius: var(--radius-md);
  }
`;

export const HeroTrust = styled.div`
  display: flex;
  gap: 28px;
  margin-top: 32px;

  @media (max-width: 900px) {
    justify-content: center;
  }
`;

export const TrustItem = styled.div`
  display: flex;
  flex-direction: column;

  strong {
    font-size: var(--text-2xl);
    font-weight: 700;
    color: var(--primary);
    line-height: 1;
  }

  span {
    font-size: var(--text-xs);
    color: var(--text-muted);
    margin-top: 2px;
  }
`;

export const HeroRight = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  position: relative;
  z-index: 1;

  @media (max-width: 900px) {
    display: none;
  }
`;

export const HeroCircle = styled.div`
  width: 320px;
  height: 320px;
  border-radius: 50%;
  background: var(--bg-card);
  border: 2px solid var(--border-light);
  box-shadow: var(--shadow-md);
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  animation: ${float} 6s ease-in-out infinite;
`;

export const HeroCircleInner = styled.div`
  font-size: 100px;
  user-select: none;
  filter: drop-shadow(0 8px 16px var(--shadow-color));
`;

export const FloatCard = styled.div`
  position: absolute;
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-md);
  padding: 10px 14px;
  box-shadow: var(--shadow-float);
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary);
  white-space: nowrap;

  &:nth-child(2) { top: 20px; right: -24px; }
  &:nth-child(3) { bottom: 36px; left: -28px; }

  .dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }
`;

/* ─── Strip de stats ────────────────────────────────────────────────────── */

export const StatsStrip = styled.div`
  background: var(--bg-page);
  border-bottom: 1px solid var(--border-light);
`;

export const StatsInner = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

export const StatItem = styled.div`
  padding: 28px 20px;
  text-align: center;
  border-right: 1px solid var(--border-light);

  &:last-child { border-right: none; }

  @media (max-width: 600px) {
    border-right: none;
    border-bottom: 1px solid var(--border-light);
    &:last-child { border-bottom: none; }
    padding: 20px;
  }
`;

export const StatNumber = styled.div`
  font-size: var(--text-4xl);
  font-weight: 700;
  color: var(--primary);
  line-height: 1;
  margin-bottom: 4px;
`;

export const StatLabel = styled.div`
  font-size: var(--text-sm);
  color: var(--text-secondary);
`;

/* ─── HOW IT WORKS ──────────────────────────────────────────────────────── */

export const HowSection = styled.section`
  background: var(--bg-section);
  padding: 80px 0;
`;

export const HowGrid = styled.div`
  max-width: 1200px;
  margin: 48px auto 0;
  padding: 0 24px;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }
`;

export const HowStep = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  padding: 32px 24px;
  text-align: center;
  box-shadow: var(--shadow-sm);
  position: relative;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);

  &:hover {
    transform: translateY(-4px);
    box-shadow: var(--shadow-hover);
  }
`;

export const StepNumber = styled.div`
  width: 44px;
  height: 44px;
  background: var(--primary);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: var(--text-base);
  font-weight: 700;
  color: var(--text-on-primary);
  margin: 0 auto 16px;
`;

export const StepIcon = styled.div`
  font-size: 36px;
  margin-bottom: 14px;
`;

export const StepTitle = styled.h3`
  font-size: var(--text-lg);
  color: var(--text-primary);
  margin-bottom: 8px;
  font-weight: 600;
`;

export const StepDesc = styled.p`
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: 1.6;
`;

/* ─── Actividades destacadas ─────────────────────────────────────────────── */

export const FeaturedSection = styled.section`
  background: var(--bg-page);
  padding: 80px 0 96px;

  @media (max-width: 768px) {
    padding: 56px 0 72px;
  }
`;

export const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 52px;
`;

export const SectionLabel = styled.span`
  display: inline-block;
  background: var(--bg-section);
  color: var(--primary);
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: 2px;
  text-transform: uppercase;
  padding: 5px 14px;
  border-radius: var(--radius-round);
  margin-bottom: 14px;

  /* sobre el fondo hielo de "Cómo funciona" la etiqueta va blanca */
  ${HowSection} & {
    background: var(--bg-card);
    border: 1px solid var(--border-light);
  }
`;

export const SectionTitle = styled.h2`
  font-size: var(--text-3xl);
  color: var(--text-primary);
  margin-bottom: 10px;
`;

export const SectionDesc = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-base);
  max-width: 480px;
  margin: 0 auto;
`;

export const ActivityGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 28px;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;

  @media (max-width: 1024px) { grid-template-columns: repeat(2, 1fr); }
  @media (max-width: 640px)  { grid-template-columns: 1fr; }
`;

export const ActivityCard = styled.div`
  background: var(--bg-card);
  border: 1px solid var(--border-light);
  border-radius: var(--radius-lg);
  overflow: hidden;
  cursor: pointer;
  transition: transform var(--transition-normal), box-shadow var(--transition-normal);
  box-shadow: var(--shadow-card);

  &:hover {
    transform: translateY(-8px);
    box-shadow: var(--shadow-hover-lg);
  }
`;

export const ActivityImageWrap = styled.div`
  position: relative;
  overflow: hidden;
  height: 210px;
`;

export const ActivityImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.5s ease;

  ${ActivityCard}:hover & {
    transform: scale(1.06);
  }
`;

export const ActivityBadge = styled.span`
  position: absolute;
  top: 12px; left: 12px;
  background: var(--primary);
  color: var(--text-on-primary);
  font-size: var(--text-xs);
  font-weight: 700;
  padding: 4px 10px;
  border-radius: var(--radius-round);
`;

export const ActivityInfo = styled.div`
  padding: 20px;
`;

export const ActivityTitle = styled.h3`
  font-size: var(--text-lg);
  color: var(--text-primary);
  margin-bottom: 6px;
  font-weight: 600;
  line-height: 1.3;
`;

export const ActivityDescription = styled.p`
  color: var(--text-secondary);
  font-size: var(--text-sm);
  line-height: 1.55;
  margin-bottom: 16px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

export const ActivityMeta = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 14px;
  border-top: 1px solid var(--border-light);
`;

export const ActivityPrice = styled.span`
  font-size: var(--text-xl);
  font-weight: 700;
  color: var(--secondary);
`;

export const ActivityDetail = styled.span`
  font-size: var(--text-xs);
  color: var(--text-muted);
  display: flex;
  align-items: center;
  gap: 3px;
`;

/* ─── CTA strip ─────────────────────────────────────────────────────────── */

export const CtaStrip = styled.section`
  background: var(--bg-dark);
  padding: 72px 24px;
  text-align: center;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -100px; right: -100px;
    width: 400px; height: 400px;
    border-radius: 50%;
    background: radial-gradient(circle, var(--accent-glow) 0%, transparent 70%);
  }

  h2 {
    font-size: var(--text-3xl);
    color: var(--text-on-dark);
    margin-bottom: 12px;
    position: relative; z-index: 1;
  }

  p {
    color: var(--text-on-dark-muted);
    font-size: var(--text-base);
    margin-bottom: 32px;
    max-width: 460px;
    margin-left: auto;
    margin-right: auto;
    position: relative; z-index: 1;
  }

  > * { position: relative; z-index: 1; }

  :focus-visible { outline-color: var(--accent); }
`;
